import { AxiosError } from 'axios';
import { AppError, ErrorType } from '../types/error';

// Error message mappings in Turkish
export const ERROR_MESSAGES: Record<ErrorType, string> = {
  // Authentication errors
  [ErrorType.AUTH_FAILED]: 'Giriş başarısız. Telefon numarası ve şifrenizi kontrol edin.',
  [ErrorType.TOKEN_EXPIRED]: 'Oturumunuz sona erdi. Lütfen tekrar giriş yapın.',
  [ErrorType.UNAUTHORIZED]: 'Bu işlemi gerçekleştirmek için yetkiniz bulunmamaktadır.',

  // Validation errors
  [ErrorType.VALIDATION_ERROR]: 'Girilen bilgilerde hata var. Lütfen kontrol edin.',
  [ErrorType.REQUIRED_FIELD]: 'Zorunlu alanları doldurun.',
  [ErrorType.INVALID_FORMAT]: 'Geçersiz format. Lütfen doğru formatta girin.',

  // CRUD operation errors
  [ErrorType.CREATE_FAILED]: 'Ekleme işlemi başarısız oldu.',
  [ErrorType.UPDATE_FAILED]: 'Güncelleme işlemi başarısız oldu.',
  [ErrorType.DELETE_FAILED]: 'Silme işlemi başarısız oldu.',
  [ErrorType.FETCH_FAILED]: 'Veriler yüklenirken hata oluştu.',

  // Network errors
  [ErrorType.NETWORK_ERROR]: 'Bağlantı hatası. İnternet bağlantınızı kontrol edin.',
  [ErrorType.TIMEOUT_ERROR]: 'İşlem zaman aşımına uğradı. Lütfen tekrar deneyin.',
  [ErrorType.SERVER_ERROR]: 'Sunucu hatası. Lütfen daha sonra tekrar deneyin.',

  // User errors
  [ErrorType.USER_NOT_FOUND]: 'Kullanıcı bulunamadı.',
  [ErrorType.DUPLICATE_USER]: 'Bu telefon numarası ile kayıtlı kullanıcı zaten var.',

  // Product errors
  [ErrorType.PRODUCT_NOT_FOUND]: 'Ürün bulunamadı.',
  [ErrorType.INVALID_CATEGORY]: 'Geçersiz kategori seçimi.',

  // General errors
  [ErrorType.UNKNOWN_ERROR]: 'Beklenmeyen bir hata oluştu.',
  [ErrorType.PERMISSION_DENIED]: 'İşlem reddedildi. Yetkiniz bulunmamaktadır.',
};

// Convert Axios error to AppError
export const parseAxiosError = (error: AxiosError): AppError => {
  const status = error.response?.status;
  const responseData = error.response?.data as any;

  // Check if server returned specific error info
  if (responseData?.error) {
    return {
      type: getErrorTypeFromMessage(responseData.error),
      message: responseData.error,
      originalError: error,
      context: {
        status,
        url: error.config?.url,
        method: error.config?.method,
      },
    };
  }

  // Map status codes to error types
  let errorType: ErrorType;
  switch (status) {
    case 400:
      errorType = ErrorType.VALIDATION_ERROR;
      break;
    case 401:
      errorType = ErrorType.UNAUTHORIZED;
      break;
    case 403:
      errorType = ErrorType.PERMISSION_DENIED;
      break;
    case 404:
      errorType = ErrorType.USER_NOT_FOUND; // or PRODUCT_NOT_FOUND based on context
      break;
    case 409:
      errorType = ErrorType.DUPLICATE_USER;
      break;
    case 500:
      errorType = ErrorType.SERVER_ERROR;
      break;
    case undefined:
      errorType = ErrorType.NETWORK_ERROR;
      break;
    default:
      errorType = ErrorType.UNKNOWN_ERROR;
  }

  return {
    type: errorType,
    message: ERROR_MESSAGES[errorType],
    originalError: error,
    context: {
      status,
      url: error.config?.url,
      method: error.config?.method,
    },
  };
};

// Try to determine error type from error message
const getErrorTypeFromMessage = (message: string): ErrorType => {
  const lowerMessage = message.toLowerCase();

  if (lowerMessage.includes('token') || lowerMessage.includes('expired')) {
    return ErrorType.TOKEN_EXPIRED;
  }
  if (lowerMessage.includes('unauthorized') || lowerMessage.includes('yetkisiz')) {
    return ErrorType.UNAUTHORIZED;
  }
  if (lowerMessage.includes('not found') || lowerMessage.includes('bulunamadı')) {
    return ErrorType.USER_NOT_FOUND;
  }
  if (lowerMessage.includes('duplicate') || lowerMessage.includes('zaten var')) {
    return ErrorType.DUPLICATE_USER;
  }
  if (lowerMessage.includes('validation') || lowerMessage.includes('geçersiz')) {
    return ErrorType.VALIDATION_ERROR;
  }

  return ErrorType.UNKNOWN_ERROR;
};

// Generate user-friendly error message
export const getErrorMessage = (error: AppError | Error | string): string => {
  if (typeof error === 'string') {
    return error;
  }

  if ('type' in error) {
    // It's an AppError
    return error.message || ERROR_MESSAGES[error.type] || ERROR_MESSAGES[ErrorType.UNKNOWN_ERROR];
  }

  // It's a generic Error
  return error.message || ERROR_MESSAGES[ErrorType.UNKNOWN_ERROR];
};

// Create specific error types for common operations
export const createCrudError = (
  operation: 'create' | 'update' | 'delete' | 'fetch',
  originalError?: Error
): AppError => {
  const errorTypeMap = {
    create: ErrorType.CREATE_FAILED,
    update: ErrorType.UPDATE_FAILED,
    delete: ErrorType.DELETE_FAILED,
    fetch: ErrorType.FETCH_FAILED,
  };

  return {
    type: errorTypeMap[operation],
    message: ERROR_MESSAGES[errorTypeMap[operation]],
    originalError,
  };
};

// Create authentication error
export const createAuthError = (message?: string): AppError => ({
  type: ErrorType.AUTH_FAILED,
  message: message || ERROR_MESSAGES[ErrorType.AUTH_FAILED],
});

// Create validation error
export const createValidationError = (field?: string, details?: string): AppError => ({
  type: ErrorType.VALIDATION_ERROR,
  message: ERROR_MESSAGES[ErrorType.VALIDATION_ERROR],
  context: { field, details },
});

// Log error for debugging
export const logError = (error: AppError | Error, context?: string) => {
  const timestamp = new Date().toISOString();
  const errorInfo = {
    timestamp,
    context,
    error: 'type' in error ? error : { message: error.message, stack: error.stack },
  };

  console.error('🚨 Error Log:', errorInfo);

  // In production, you might want to send this to an error tracking service
  // if (process.env.NODE_ENV === 'production') {
  //   sendToErrorTracking(errorInfo)
  // }
};
